import React from "react";

import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTheme } from "@mui/material/styles";

import { HelpBox } from "@/components/atoms";
import { interpolateDivergant, toIconLabel, toIndex, toPercentage } from "@/utils";
import { MindsetResult } from "@/types";

interface MindsetInsightCardProps {
  mindset: MindsetResult;
  score: string;
  minValue?: number;
  maxValue?: number;
}

const MindsetInsightCard: React.FC<MindsetInsightCardProps> = (
  {
    mindset,
    score,
    minValue,
    maxValue
  }) => {
  const theme = useTheme();
  const formatFunc = score === "rate" ? toPercentage : toIndex;

  const src = `https://sc-opendata.s3.eu-west-1.amazonaws.com/mindset_images/${toIconLabel(mindset.tag_name)}.png`
  const value = Number(mindset[score as keyof MindsetResult]);

  const calcPercentage = (value: number) => {
    const mx = maxValue ? maxValue : 1;
    const mn = minValue ? minValue : 0;
    return Math.max(0.01, (value - mn) / (mx - mn));
  };

  return (
    <Grid key={mindset.tag_name}>
      <Card
        sx={{
          border: 1,
          borderRadius: 1,
          borderColor: theme.palette.divider,
          width: "300px",
        }}
      >
        <CardContent
          sx={{
            pb: 2,
            "&:last-child": { pb: 2 },
          }}
        >
          <Stack spacing={2} direction="row" alignItems="center" width="100%">
            <Box
              component="img"
              // src={variable.image}
              src={src}
              alt={mindset.tag_name}
              height={60}
              width={60}
              display="block"
              style={{
                borderRadius: 5,
                objectFit: "cover"
              }}
            ></Box>
            <Stack spacing={0} width="100%">
              <HelpBox title={mindset.tag_name} placement="top">
                <Typography
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: "1",
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {mindset.tag_name}
                </Typography>
              </HelpBox>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: "1",
                  WebkitBoxOrient: "vertical",
                }}
              >
                {mindset.tag_category}
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
              >
                <Box
                  height="4px"
                  borderRadius="4px"
                  width={`${calcPercentage(value) * 100}%`}
                  sx={{
                    backgroundColor: interpolateDivergant(
                      theme.palette.score.light,
                      theme.palette.score.main,
                      theme.palette.score.dark,
                      1 - calcPercentage(value),
                    ),
                  }}
                />
                <Typography
                  sx={{
                    color: interpolateDivergant(
                      theme.palette.score.light,
                      theme.palette.score.main,
                      theme.palette.score.dark,
                      1 - calcPercentage(value),
                    ),
                  }}
                >
                  {formatFunc(value)}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default MindsetInsightCard;
